import React, { useState } from 'react';

import { Button, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import AssistantIcon from '@material-ui/icons/Assistant';
import FlagIcon from '@material-ui/icons/Flag';
import PolicyIcon from '@material-ui/icons/PolicyOutlined';
import RateActivity from './RateActivity';
import CreatedAt from './CreatedAt';
import CallRecording from './CallRecording';
import styled from 'styled-components';
import {
  Call,
  Email,
  Message,
  Note,
  CallTypes,
  CommunicationTypes,
  ConciergeActions,
  Communication,
  CommunicationType,
  VoicemailDrop,
  VoicemailDropStatuses,
} from 'typings';
import { useDispatch } from 'react-redux';
import { apiFlagMessage } from 'state/lead.state';
import { ReactComponent as LaunchIcon } from 'assets/images/launch-24px.svg';
import Zone from 'components/shared/Zone';
import Tooltip from '@material-ui/core/Tooltip';

interface Props {
  type: CommunicationType;
  communication: Communication;
  isAdminAction: boolean;
  isRevealed: boolean;
}

interface DebugInfoProps {
  open: boolean;
  onClose: (event: any) => void;
  debugInfo?: string;
}

interface EmailDialogProps {
  open: boolean;
  onClose: (event: any) => void;
  subject?: string;
  text?: string;
}

const StyledIconButton = styled(IconButton)`
  color: inherit;
  padding: 0;

  &:hover {
    background-color: inherit;
  }
`;

const Spacer = styled('span')`
  display: inline-block;
  padding: ${(props) => props.theme.spacing(0, 0.5)};
`;

const CaptionBase = styled(Typography)`
  font-weight: 500;
  padding: ${(props) => props.theme.spacing(0.5, 1)};
  color: ${(props) => props.theme.palette.grey['500']};

  & svg {
    display: inline-flex;
    width: ${(props) => props.theme.spacing(2)};
  }
`;

const SmsCaption = styled(CaptionBase).attrs<{ incoming: boolean }>((props) => ({}))<{
  incoming: boolean;
}>`
  display: inline-flex;
  align-items: center;
  text-align: ${(props) => (props.incoming ? 'right' : 'inherit')};
`;

const EmailCaption = styled(CaptionBase)`
  display: flex;
  align-items: center;
  padding: 0;
  text-transform: none;

  .MuiButton-root {
    flex-grow: 1;
    justify-content: flex-start;
    padding: 0;
    &:hover {
      background-color: inherit;
    }
  }

  & svg {
    display: inline-flex;
    width: ${(props) => props.theme.spacing(2)};
  }
`;

const AutomationFlagButton = styled(Button).attrs<{ isFlagged: boolean }>((props) => ({}))<{
  isFlagged: boolean;
}>`
  display: inline-flex;
  align-items: center;
  font-size: var(--s-2);
  text-transform: none;
  height: ${(props) => props.theme.spacing(1)};
  padding: 0;
  text-decoration: ${(props) => (props.isFlagged ? 'none' : 'underline')};
  color: ${(props) => (props.isFlagged ? 'red' : 'inherit')};
  &:hover {
    background-color: inherit;
  }
  & svg {
    display: inline-flex;
    width: ${(props) => props.theme.spacing(1.5)};
  }
`;

const getAuthor = (communicationType: CommunicationType, communication: Communication): string => {
  let author;

  switch (communicationType) {
    case CommunicationTypes.NOTE:
      const note = communication as Note;
      author = note.conciergeName;
      break;
    case CommunicationTypes.SMS:
      const sms = communication as Message;
      author = sms.incoming ? sms.leadName : sms.authorDisplay || '';
      break;
    case CommunicationTypes.CALL:
      const call = communication as Call;
      author = call.callType === CallTypes.INBOUND ? call.leadName : call.conciergeName;
      break;
    case CommunicationTypes.EMAIL:
      const email = communication as Email;
      author = email.incoming ? email.leadName : email.conciergeName || ConciergeActions.AUTOMATIC;
      break;
    case CommunicationTypes.VOICEMAIL_DROP:
      author = 'Alex Parker';
      break;
    default:
      break;
  }

  return author ?? '';
};

const getCaptionType = (communicationType: CommunicationType) => {
  switch (communicationType) {
    case CommunicationTypes.SMS:
      return SmsCaption;
    case CommunicationTypes.EMAIL:
      return EmailCaption;
    case CommunicationTypes.CALL:
    case CommunicationTypes.NOTE:
    default:
      return CaptionBase;
  }
};

const SmsCaptionComponent = ({ sms }: { sms: Message }) => {
  const dispatch = useDispatch();

  const showFailedSmsCaption = !!sms.sendError;
  const showAutomationFlag = sms.concierge?.isSystem ?? false;

  const handleFlagMessage = () => {
    const { id, isFlagged } = sms;
    dispatch(
      apiFlagMessage({
        messageId: id,
        isFlagged: !isFlagged,
      })
    );
  };

  return (
    <>
      {showAutomationFlag && (
        <>
          <AutomationFlagButton isFlagged={sms.isFlagged} onClick={handleFlagMessage}>
            <FlagIcon />
            {sms.isFlagged ? 'Issue reported' : 'Report issue'}
          </AutomationFlagButton>
          <Spacer>&middot;</Spacer>
        </>
      )}
      {showFailedSmsCaption && (
        <>
          <Tooltip title={sms.sendError}>
            <div>
              <span className="error-text">FAILED TO SEND</span>&nbsp;&middot;&nbsp;
            </div>
          </Tooltip>
        </>
      )}
    </>
  );
};

const VoicemailDropCaptionComponent = ({ voicemailDrop }: { voicemailDrop: VoicemailDrop }) => {
  return (
    <>
      <span>{voicemailDrop.status.toUpperCase()}</span>
      &nbsp;&middot;&nbsp;
    </>
  );
};

const EmailDialog = ({ open, onClose, subject, text }: EmailDialogProps) => {
  return (
    <Dialog onClose={onClose} open={open}>
      <Zone>
        <DialogContent>
          <Typography variant="h6">Subject:</Typography>
          <Typography variant="body2">{subject}</Typography>
          <Typography variant="h6">Message:</Typography>
          <Typography variant="body2">{text}</Typography>
        </DialogContent>
      </Zone>
    </Dialog>
  );
};

const EmailCaptionComponent = ({ email }: { email: Email }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <EmailDialog open={open} onClose={handleClose} subject={email.subject} text={email.text} />
      <Button onClick={handleClickOpen}>
        <LaunchIcon />
        <Typography variant="caption" className="message-caption">
          View Full Email
        </Typography>
      </Button>
    </>
  );
};

const DebugInfoDialog = ({ open, onClose, debugInfo }: DebugInfoProps) => {
  return (
    <Dialog onClose={onClose} open={open}>
      <Zone>
        <DialogContent>
          <Typography variant="h6">Debug Info:</Typography>
          <pre>{debugInfo}</pre>
        </DialogContent>
      </Zone>
    </Dialog>
  );
};

const DebugInfoComponent = ({ debugInfo }: { debugInfo: string }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <DebugInfoDialog open={open} onClose={handleClose} debugInfo={debugInfo} />
      <Tooltip title="View debug info" arrow={true} placement={'bottom'}>
        <StyledIconButton onClick={handleClickOpen}>
          <PolicyIcon />
        </StyledIconButton>
      </Tooltip>
    </>
  );
};

const Caption = ({ type, communication, isAdminAction, isRevealed }: Props) => {
  const CaptionComponent = getCaptionType(type);
  let debugInfo = null;
  let isAiGenerated = false;
  let isCensored = false;

  if ('debugInfo' in communication) {
    debugInfo = communication.debugInfo;
  }

  if ('hasIssue' in communication) {
    isCensored = !!communication.hasIssue;
  }

  if ('isAiGenerated' in communication) {
    isAiGenerated = !!communication.isAiGenerated;
  }

  return (
    <CaptionComponent variant="caption">
      {isAdminAction && !!debugInfo && (
        <>
          <DebugInfoComponent debugInfo={debugInfo} />
          <Spacer>&middot;</Spacer>
        </>
      )}
      {type === CommunicationTypes.SMS && <SmsCaptionComponent sms={communication as Message} />}
      {type === CommunicationTypes.EMAIL && (isRevealed || !isCensored) && (
        <EmailCaptionComponent email={communication as Email} />
      )}
      {type === CommunicationTypes.VOICEMAIL_DROP && (
        <VoicemailDropCaptionComponent voicemailDrop={communication as VoicemailDrop} />
      )}
      {isAdminAction && type !== CommunicationTypes.VOICEMAIL_DROP && (
        <>
          <RateActivity activity={communication} />
          <Spacer>&middot;</Spacer>
        </>
      )}
      {getAuthor(type, communication)}
      {isAiGenerated && (
        <>
          <Spacer>&middot;</Spacer>
          <Tooltip
            title="The AI assistant was used on this distribution, however the message may have been modified"
            arrow={true}
            placement={'bottom'}
          >
            <AssistantIcon fontSize="small" />
          </Tooltip>
        </>
      )}
      <Spacer>&middot;</Spacer>
      <CreatedAt time={communication.createdAt} />
      {isAdminAction &&
        type === CommunicationTypes.CALL &&
        'recordingUrl' in communication &&
        communication.providerRecordingId &&
        communication.recordingUrl && (
          <>
            <Spacer>&middot;</Spacer>
            <CallRecording
              audioUrl={communication.recordingUrl}
              providerRecordingId={communication.providerRecordingId}
            />
          </>
        )}
    </CaptionComponent>
  );
};

export default Caption;
